// eslint-disable-next-line eag/match-named-export -- Muted pre-existing violation
import {isNil, isNotNil, keys} from 'ramda';

import type {TextEditorSelection} from '../../types/TextEditorApi';
import {getSizingData} from '../Textarea/utils/getSizingData';

/** A DOM range over text nodes, not editor markup or serialized HTML offsets. */
export const getTextNodeRange = (
  root: HTMLElement,
  selection: TextEditorSelection
): Range | null => {
  if (selection.index < 0 || selection.length <= 0) {
    return null;
  }

  const end = selection.index + selection.length;
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const range = root.ownerDocument.createRange();
  let offset = 0;
  let hasStart = false;
  let node = walker.nextNode();

  while (isNotNil(node)) {
    const length = node.textContent?.length ?? 0;
    if (!hasStart && selection.index <= offset + length) {
      range.setStart(node, selection.index - offset);
      hasStart = true;
    }
    if (hasStart && end <= offset + length) {
      range.setEnd(node, end - offset);
      return range;
    }
    offset += length;
    node = walker.nextNode();
  }

  return null;
};

/**
 * The rectangles of the text alone. One range across several paragraphs also reports the
 * full-width box of every paragraph it covers, which painted empty lines as solid bands.
 */
export const getTextNodeRects = (root: HTMLElement, selection: TextEditorSelection): DOMRect[] => {
  if (selection.index < 0 || selection.length <= 0) {
    return [];
  }

  const end = selection.index + selection.length;
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const rects: DOMRect[] = [];
  let offset = 0;
  let node = walker.nextNode();

  while (isNotNil(node) && offset < end) {
    const length = node.textContent?.length ?? 0;
    const start = Math.max(selection.index, offset);
    const stop = Math.min(end, offset + length);
    if (stop > start) {
      const range = root.ownerDocument.createRange();
      range.setStart(node, start - offset);
      range.setEnd(node, stop - offset);
      rects.push(...(range.getClientRects ? Array.from(range.getClientRects()) : []));
    }
    offset += length;
    node = walker.nextNode();
  }

  return rects;
};

/** Textareas expose offsets but no text rectangles. Measure a read-only, metric-matched mirror. */
export const getTextareaRangeRects = (
  editor: HTMLTextAreaElement,
  selections: TextEditorSelection[]
): DOMRect[] => {
  const sizing = getSizingData(editor);
  if (isNil(sizing)) {
    return [];
  }

  const document = editor.ownerDocument;
  const mirror = document.createElement('div');
  keys(sizing.sizingStyle).forEach((key) => {
    mirror.style[key] = sizing.sizingStyle[key];
  });
  const bounds = editor.getBoundingClientRect();
  Object.assign(mirror.style, {
    position: 'fixed',
    left: `${bounds.left + editor.clientLeft - editor.scrollLeft}px`,
    top: `${bounds.top + editor.clientTop - editor.scrollTop}px`,
    width: `${editor.clientWidth}px`,
    height: 'auto',
    boxSizing: 'border-box',
    border: '0',
    direction: window.getComputedStyle(editor).direction,
    whiteSpace: editor.wrap === 'off' ? 'pre' : 'pre-wrap',
    overflowWrap: editor.wrap === 'off' ? 'normal' : 'break-word',
    visibility: 'hidden',
    pointerEvents: 'none',
  });
  mirror.setAttribute('aria-hidden', 'true');
  mirror.textContent = editor.value;
  document.body.appendChild(mirror);

  try {
    return selections.flatMap((selection) => {
      const range = getTextNodeRange(mirror, selection);
      return range?.getClientRects ? Array.from(range.getClientRects()) : [];
    });
  } finally {
    mirror.remove();
  }
};
